import {
  DownArrowIcon,
  DownloadIcon,
  RightTailArrowIcon,
  searchFilter as SearchFilterIcon,
  UpArrowIcon,
} from '@/assets/icons/icons';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Divider, Empty, Flex, Menu, message, Spin } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CustomPagination, CustomTitle, SearchInput } from '../../components';

import {
  fetchShipmentLegs,
  fetchShipmentLegsDownload,
  type ShipmentLegListModel,
} from '@/apis/shipment';
import type { ShipmentLegListItem } from '@/apis/types';
import { useToken } from '@/common/helper';
import { useSpin } from '@/components/SpinContext';
import useUnauthorize from '@/hooks/useUnauthorize';
import TrackingStatusCard from './components/TrackingStatusCard';
import { saveBlobAsFile } from './components/fileDownload';

interface ShipmentLegsListDrawerProps {
  toggleDrawer: (value: boolean | null, name?: string | null) => void;
  shipmentId: string;
  initialLegType?: LegType;
  onLegClick?: (leg: ShipmentLegListItem) => void;
}

type LegType = 'CL' | 'EL';

const normalizeDrawerLeg = (leg: ShipmentLegListItem) => {
  return {
    id: leg.id,
    shipment_id: leg.shipment_id,
    leg_type: leg.leg_type,
    courier_name: leg.courier_name,
    reference_number: leg.reference || '',
    status: leg.status || '',
    type: String(leg?.type ?? 5),
    alert_count: leg.alert_count || 0,
    alert_status: [] as string[],
    from_location: leg.from_location ? leg.from_location : null,
    to_location: leg.to_location ? leg.to_location : null,
    estimated_delivery_date: leg.eta || undefined,
    leg_mode: leg.leg_mode,
  };
};

const SORT_OPTIONS: Record<LegType, Array<{ key: string; label: string }>> = {
  CL: [
    { key: 'reference', label: 'Customer reference' },
    { key: 'job_number', label: 'Job number' },
    { key: 'status', label: 'Status' },
    { key: 'execution_leg_count', label: 'Execution leg count' },
    { key: 'created_at', label: 'Created at' },
  ],
  EL: [
    { key: 'mbl', label: 'MBL' },
    { key: 'mawb', label: 'MAWB' },
    { key: 'mode', label: 'Mode' },
    { key: 'status', label: 'Status' },
    { key: 'eta', label: 'ETA' },
    { key: 'alert_count', label: 'Alerts' },
    { key: 'co_loaded_count', label: 'Co-load count' },
  ],
};

const buildShipmentPayload = (shipmentId: string) => {
  try {
    const parsed = shipmentId?.trim().startsWith('{')
      ? JSON.parse(shipmentId)
      : null;
    if (parsed && typeof parsed === 'object' && 'select_all' in parsed) {
      return parsed.select_all === true
        ? {
            select_all: true,
            exclude_shipment_ids: parsed.exclude_shipment_ids || [],
            filters: parsed.filters || {},
          }
        : { select_all: false, shipment_ids: parsed.shipment_ids || [] };
    }
  } catch {
    return { shipment_id: shipmentId };
  }
  return { shipment_id: shipmentId };
};

const ShipmentLegsListDrawer: React.FC<ShipmentLegsListDrawerProps> = ({
  toggleDrawer,
  shipmentId,
  initialLegType = 'CL',
  onLegClick,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [legs, setLegs] = useState<ShipmentLegListItem[]>([]);
  const [legCount, setLegCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const legType = initialLegType;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);
  const [pageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortOrder, setSortOrder] = useState<'ascending' | 'descending'>(
    'ascending'
  );
  const [sortKey, setSortKey] = useState<string>(
    initialLegType === 'CL' ? 'reference' : 'mbl'
  );

  const isMultipleShipments = shipmentId?.trim().startsWith('{');

  const buildModel = useCallback(
    (overrides: Partial<ShipmentLegListModel> = {}): ShipmentLegListModel => ({
      ...buildShipmentPayload(shipmentId),
      leg_type: legType,
      search: searchQuery,
      sort_key: sortKey,
      sort_order: sortOrder,
      page,
      page_size: pageSize,
      ...overrides,
    }),
    [legType, page, pageSize, searchQuery, shipmentId, sortKey, sortOrder]
  );

  const fetchLegLists = useCallback(() => {
    setIsLoading(true);
    fetchShipmentLegs(token, buildModel())
      .then((res) => {
        const data = res?.data?.data;
        if (!data?.is_success) {
          message.error(data?.message);
          return;
        }
        const payload = data?.data;
        setLegs(payload?.legs || []);
        setLegCount(payload?.legs_count || 0);
        setTotalPages(payload?.page_count || 1);
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [buildModel, token, unauthorize]);

  useEffect(() => {
    fetchLegLists();
  }, [fetchLegLists]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const isDropdownClick =
        target.closest('.ant-dropdown') ||
        target.closest('.ant-dropdown-menu') ||
        target.closest('.ant-select') ||
        target.closest('.ant-select-dropdown');
      if (isDropdownClick) return;
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)
      ) {
        toggleDrawer(null, null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [toggleDrawer]);

  const applySearch = (value: string = searchDraft) => {
    setSearchQuery(value.trim());
    setPage(1);
  };

  const clearSearch = () => {
    setIsSearchVisible(false);
    setSearchDraft('');
    setSearchQuery('');
    setPage(1);
  };

  const handleSortOrderToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSortOrder((prev) => (prev === 'ascending' ? 'descending' : 'ascending'));
  };

  const downloadCSV = () => {
    showSpin();
    fetchShipmentLegsDownload(
      token,
      buildModel({ file_type: 'csv', page: 1, page_size: 0 })
    )
      .then((res) => {
        saveBlobAsFile(
          { data: res.data, headers: res.headers as Record<string, string> },
          `${legType.toLowerCase()}-legs-${new Date()
            .toISOString()
            .slice(0, 10)}.csv`
        );
      })
      .catch((error) => {
        message.error('Unable to download legs list.');
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const sortMenu = (
    <Menu
      selectedKeys={[sortKey]}
      onClick={({ key }) => {
        setSortKey(String(key));
        setPage(1);
      }}
    >
      {SORT_OPTIONS[legType].map((opt) => (
        <Menu.Item style={{ color: 'var(--text-primary)' }} key={opt.key}>
          {opt.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div className='h-full shipment-legs-list-drawer' ref={drawerRef}>
      <Flex gap={16} justify='space-between' align='center'>
        <span
          className='c-pointer'
          style={{ marginTop: '4px' }}
          onClick={() => {
            if (isMultipleShipments) {
              toggleDrawer(null, null);
            } else {
              toggleDrawer(true, 'Detail');
            }
          }}
        >
          <RightTailArrowIcon />
        </span>
        <Flex gap={18} align='center'>
          <button
            type='button'
            title='Download CSV'
            aria-label='Download legs CSV'
            className='shipment-legs-list-icon-button'
            onClick={downloadCSV}
          >
            <DownloadIcon />
          </button>
          <button
            type='button'
            title='Close'
            aria-label='Close legs drawer'
            className='shipment-legs-list-icon-button'
            onClick={() => toggleDrawer(null, 'all')}
          >
            <CloseOutlined
              style={{ fontSize: 14, color: 'var(--text-primary)' }}
            />
          </button>
        </Flex>
      </Flex>

      <CustomTitle
        text={`${legType === 'CL' ? 'Customer legs' : 'Execution legs'} (${legCount})`}
        fontSize={18}
        fontWeight={700}
        lineHeight='24px'
        color='var(--text-primary)'
        marginTop='24px'
        marginBottom='0'
      />

      <Divider style={{ margin: '8px 0' }} />

      <Flex
        justify='space-between'
        align='center'
        gap={12}
        style={{ margin: '8px 0' }}
      >
        <button
          type='button'
          className={`search-toggle-btn close-alert-sidebar-icon ${
            isSearchVisible ? 'open' : ''
          }`}
          aria-label={
            isSearchVisible ? 'Apply legs search' : 'Open legs search'
          }
          onClick={() => {
            if (isSearchVisible) {
              applySearch();
            } else {
              setIsSearchVisible(true);
            }
          }}
        >
          <SearchOutlined className='search-toggle-icon' />
        </button>
        {isSearchVisible ? (
          <SearchInput
            borderWidth='2px'
            placeholder=''
            defaultValue={searchDraft}
            onChange={setSearchDraft}
            onClear={clearSearch}
            style={{ flex: 1, paddingRight: 10 }}
            prefixIcon={<SearchFilterIcon width={12} height={12} />}
            onPressEnter={applySearch}
            autoFocus
          />
        ) : null}
        <Dropdown overlay={sortMenu} trigger={['click']}>
          <Flex gap={6} align='center' className='cursor-pointer'>
            <CustomTitle
              text='Sort by:'
              fontWeight='400'
              fontSize='12px'
              lineHeight='20px'
              marginBottom={0}
              marginTop={0}
            />
            <CustomTitle
              text={
                SORT_OPTIONS[legType].find((o) => o.key === sortKey)?.label ||
                ''
              }
              fontWeight='600'
              fontSize='12px'
              marginBottom={0}
              marginTop={0}
            />
            <div className='sort-arrow-wrapper' onClick={handleSortOrderToggle}>
              {sortOrder === 'ascending' ? (
                <UpArrowIcon size={14} color='var(--text-primary)' />
              ) : (
                <DownArrowIcon size={14} color='var(--text-primary)' />
              )}
            </div>
          </Flex>
        </Dropdown>
      </Flex>

      <Divider style={{ margin: '8px 0' }} />

      {isLoading ? (
        <Flex
          vertical
          gap={8}
          justify='center'
          align='center'
          style={{ height: '200px' }}
        >
          <Spin size='default' />
          <span className='disabled-row font-12'>Loading...</span>
        </Flex>
      ) : (
        <Flex vertical gap={8} className='shipment-leg-list-cards'>
          {legs.length > 0 ? (
            legs.map((leg) => {
              const normalizedLeg = normalizeDrawerLeg(leg);

              return (
                <button
                  key={leg.id}
                  type='button'
                  className='shipment-legs-list-card-button'
                  aria-label={`Open ${leg.reference || leg.leg_type} details`}
                  onClick={() => onLegClick?.(leg)}
                >
                  <TrackingStatusCard legDetail={normalizedLeg} />
                </button>
              );
            })
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No data' />
          )}
        </Flex>
      )}

      {totalPages > 1 && (
        <Flex style={{ width: '100%', marginTop: '12px' }} justify='center'>
          <CustomPagination
            page={page}
            totalPages={totalPages}
            onPageChange={(newPage) => setPage(newPage > 0 ? newPage : 1)}
          />
        </Flex>
      )}
    </div>
  );
};

export default ShipmentLegsListDrawer;
